import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {CountryChartData} from '../../../../models/country-chart-data.interface';
import {CS_EUROPEAN_TREND_COLORS, POM_EUROPEAN_TREND_COLORS} from '../../../../../constants/charts';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';

interface YearCountryData {
  year: string;
  countryData: CountryChartData[];
}

@Component({
  selector: 'app-european-trend-bar-chart',
  templateUrl: './european-trend-bar-chart.component.html',
  styleUrls: ['./european-trend-bar-chart.component.scss']
})
export class EuropeanTrendBarChartComponent implements OnInit, OnChanges {

  textBelow!: string;
  chart!: Chart;
  chartData!: SeriesOptionsType[];
  countryCodes!: string[];
  countriesText = '';
  textBelowCodePOM = 'kfcharts.Country.PutOnMarket.EuropeanTrend.textBelow';
  textBelowCodeCollected = 'kfcharts.Country.Collected.EuropeanTrend.textBelow';
  isAdmin!: boolean;

  @Input()
  data!: CountryChartData[];
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;
  @Input()
  countryCategory!: string;
  @Input()
  countries!: string[];

  private static getCountryCodes(chartData: CountryChartData[]): string[] {
    const chartCodes = chartData.map(item => item.countryCode);
    return [...new Set(chartCodes)];
  }

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(
      this.countryCategory === 'putonmarket' ? this.textBelowCodePOM : this.textBelowCodeCollected).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    this.setCountriesText();
    if (this.data) {
      this.countryCodes = EuropeanTrendBarChartComponent.getCountryCodes(this.data);

      // highcharts
      this.chartData = this.convertToChartData(this.groupByYear(this.data));
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: this.countryCategory === 'putonmarket' ? POM_EUROPEAN_TREND_COLORS : CS_EUROPEAN_TREND_COLORS,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        caption: {
          text: `<strong>Countries:</strong> ${this.countriesText}`,
        },
        exporting: {
          tableCaption: this.title + ' ' + this.subtitle
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: true,
              format: '{point.custom.year}'
            }
          }
        },
        tooltip: {
          formatter(): string[] | undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              if (pointOptions.custom?.absolute === 0) {
                return [this.point.name];
              }
              return ['<div class="center"><b>' + pointOptions.custom?.year + '</b></div><br><br>',
                '- Absolute: ' + formatNumber(pointOptions.custom?.absolute) + ' tons' + '<br>',
                '- Population: ' + (pointOptions.custom?.population === null ? 'No data' : (formatNumber(pointOptions.custom?.population) + ' inhabitants')) + '<br>',
                '- Specific: ' + (pointOptions.custom?.population === null ? 'No data' :
                  (formatNumber(pointOptions.custom?.absolute * 1000 / pointOptions.custom?.population)) + ' kg/inh.') + '<br>',
              ];
            }
            // If not null, use the default formatter
            // return tooltip.defaultFormatter.call(this, tooltip);
            return undefined;
          }
        },
        xAxis: {
          visible: false,
          categories: this.countryCodes,
          title: {
            text: null
          }
        },
        legend: {
          enabled: false
        },
        series: this.chartData
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'],
      {state: {code: this.countryCategory === 'putonmarket' ? this.textBelowCodePOM : this.textBelowCodeCollected}}).then(
      () => {
        // do nothing
      }
    );
  }

  private convertToChartData(data: YearCountryData[]): SeriesOptionsType[] {
    this.chart?.ref?.hideData();
    const chartData = [] as SeriesOptionsType[];
    data.forEach(item => {
      chartData.push({
        name: item.year,
        type: 'column',
        data: item.countryData.map(dataItem => {
          return {
            y: (this.displayUnits === 'SPECIFIC' ? parseFloat(formatNumber(dataItem.absolute * 1000 / dataItem.population)) : dataItem.absolute),
            name: this.displayUnits,
            custom: {year: dataItem.year, population: dataItem.population, absolute: dataItem.absolute}
          };
        })
      });
    });
    return chartData;
  }

  private groupByYear(objectArray: Array<any>): YearCountryData[] {
    const yearCountryDataArray: YearCountryData[] = [];
    const groupedArray = objectArray.reduce((acc, obj) => {
      const key = obj.year;
      acc.set(key, obj);
      return acc;
    }, new Map());

    groupedArray.forEach((x: any) => {
      const toArray = [x];
      const yearCountryData = {} as YearCountryData;
      yearCountryData.year = x.year;
      yearCountryData.countryData = toArray;
      yearCountryDataArray.push(yearCountryData);
    });

    return yearCountryDataArray;
  }

  private setCountriesText(): void {
    this.countriesText = '';
    this.countries?.forEach((country, index) => {
      if (index === 0) {
        this.countriesText = ` ${country}`;
      } else {
        this.countriesText += `, ${country}`;
      }
    });
  }

}
